import { Injectable } from '@angular/core';
import { HttpService } from '../http/http.service';

declare const Accept: any;

@Injectable({
  providedIn: 'root'
})
export class CyberSourceService {

  private fixedIframeObserver: MutationObserver | null = null;

  constructor(private httpService: HttpService) { }

  captureContext(orderId: string): Promise<any> {
    return this.httpService.post('cybersource/capture-context', {
      order_id: orderId,
      target_origin: window.location.origin,
    });
  }

  complete(orderId: string, transientToken: string): Promise<any> {
    return this.httpService.post('cybersource/complete', {
      order_id: orderId,
      transient_token: transientToken,
    });
  }

  /**
   * Pide el capture-context, carga el SDK de Cybersource y crea la instancia
   * de pagos unificados (sin mostrarla todavía). El componente decide cuándo
   * hacer checkout.show({containers}) -esa promesa no resuelve hasta que el
   * cliente termina de cargar la tarjeta-, para poder ocultar el spinner
   * recién ahí y no durante toda la espera del usuario.
   *
   * Nota: esta cuenta de Cybersource sirve el SDK generación 0.3x
   * ("SecureAcceptance.js"), que expone Accept(jwt) -> unifiedPayments(),
   * no el VAS.UnifiedCheckout() de la guía nueva (esa API no está
   * disponible para este merchant). Este SDK solo soporta el modo
   * "sidebar" (un panel position:fixed que el propio script posiciona);
   * no acepta un contenedor paymentScreen para modo embedded.
   */
  async prepareCheckout(orderId: string): Promise<any> {
    this.startFixedIframeGuard();

    const ccResponse = await this.captureContext(orderId);
    const jwt = ccResponse && ccResponse.data && ccResponse.data.capture_context;
    if (!jwt) {
      throw new Error('No se pudo iniciar el pago.');
    }

    const payload = this.decodeJwtPayload(jwt);
    const ctxData = payload && payload.ctx && payload.ctx[0] && payload.ctx[0].data;
    if (!ctxData || !ctxData.clientLibrary) {
      throw new Error('No se pudo iniciar el pago.');
    }

    this.ensureCaptureContextInput(jwt);
    await this.loadScript(ctxData.clientLibrary, ctxData.clientLibraryIntegrity);
    await this.waitForAccept();

    const client = await Accept(jwt);
    return await client.unifiedPayments();
  }

  /**
   * El script SecureAcceptance.js dispara su evento "load" en cuanto termina
   * de descargarse, pero define window.Accept un poco después (inicialización
   * interna asíncrona). Comprobarlo una sola vez justo tras el onload es una
   * carrera que falla de forma intermitente. Reintentar con poll corto.
   */
  private waitForAccept(timeoutMs: number = 5000, intervalMs: number = 100): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        if (typeof Accept !== 'undefined') {
          resolve();
          return;
        }
        if (Date.now() - start > timeoutMs) {
          reject(new Error('El formulario de pago no está disponible en este momento. Intenta nuevamente en unos minutos.'));
          return;
        }
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  private loadScript(src: string, integrity: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      if (integrity) {
        script.integrity = integrity;
        script.crossOrigin = 'anonymous';
      }
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('No se pudo cargar el formulario de pago. Intenta nuevamente.'));
      document.head.appendChild(script);
    });
  }

  private ensureCaptureContextInput(jwt: string): void {
    let input = document.getElementById('captureContext') as HTMLInputElement;
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.id = 'captureContext';
      input.name = 'captureContext';
      document.body.appendChild(input);
    }
    input.value = jwt;
  }

  private decodeJwtPayload(jwt: string): any {
    const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  }

  /**
   * El panel de pago de Cybersource ("sidebar") se inserta como uno o mas
   * iframes position:fixed que el propio SDK (o, para Click to Pay/SRC, la
   * red de tarjetas - Visa/Mastercard, en su propio dominio) calcula y
   * posiciona por su cuenta. En algunos navegadores ese calculo queda mal
   * (el panel se corta, parte queda fuera del viewport). No restringimos
   * por dominio porque el sub-panel de reconocimiento SRC/Click to Pay no
   * necesariamente carga desde cybersource.com. Vigilamos el DOM con un
   * MutationObserver y forzamos la posicion/tamano correctos via JS apenas
   * aparece o cambia cualquier iframe cross-origin con position:fixed
   * calculada por el navegador (no confiamos en el string del atributo
   * style, que el SDK puede reescribir con formato distinto).
   */
  private startFixedIframeGuard(): void {
    if (this.fixedIframeObserver) {
      return;
    }

    const fixIframe = (iframe: HTMLIFrameElement) => {
      if (!iframe.src) {
        return;
      }
      if (getComputedStyle(iframe).position !== 'fixed') {
        return;
      }
      const set = (prop: string, value: string) => iframe.style.setProperty(prop, value, 'important');
      set('position', 'fixed');
      set('top', '0');
      set('right', '0');
      set('left', 'auto');
      set('bottom', 'auto');
      set('transform', 'none');
      set('margin', '0');
      set('width', 'min(420px, 100vw)');
      set('max-width', '100vw');
      set('height', '100vh');
      set('max-height', '100vh');
      set('overflow-y', 'auto');
      // eslint-disable-next-line no-console
      console.log('[cybersource] fixed iframe repositioned:', iframe.src.slice(0, 80));
    };

    const scanAll = () => {
      document.querySelectorAll('iframe').forEach((el) => fixIframe(el as HTMLIFrameElement));
    };

    this.fixedIframeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLIFrameElement) {
              fixIframe(node);
            } else if (node instanceof HTMLElement) {
              node.querySelectorAll('iframe').forEach((el) => fixIframe(el as HTMLIFrameElement));
            }
          });
        } else if (mutation.type === 'attributes' && mutation.target instanceof HTMLIFrameElement) {
          fixIframe(mutation.target);
        }
      }
    });

    this.fixedIframeObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    scanAll();
  }
}
